/**
 * Image processing utility for resizing and compressing images
 */

/**
 * Resizes an image so that the smaller dimension is 3000px (if larger than 3000px)
 * and converts it to JPEG with 98% quality
 * @param {File} file - The image file to process
 * @returns {Promise<File>} - The processed image file
 */
export const processImage = (file) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        let { width, height } = img;
        
        // Calculate new dimensions
        // If the smaller dimension is larger than 3000px, resize proportionally
        const minDimension = Math.min(width, height);
        if (minDimension > 3000) {
          const scale = 3000 / minDimension;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw the resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with 98% quality
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new File object with the processed image
              const processedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(processedFile);
            } else {
              reject(new Error('Failed to process image'));
            }
          },
          'image/jpeg',
          0.98 // 98% quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Processes multiple image files
 * @param {File[]} files - Array of image files to process
 * @returns {Promise<File[]>} - Array of processed image files
 */
export const processImages = async (files) => {
  try {
    const processedFiles = await Promise.all(
      files.map(file => processImage(file))
    );
    return processedFiles;
  } catch (error) {
    console.error('Error processing images:', error);
    throw error;
  }
};

/**
 * Checks if a file is an image
 * @param {File} file - The file to check
 * @returns {boolean} - True if the file is an image
 */
export const isImageFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return allowedTypes.includes(file.type);
};

// Limits for the file the user PICKS. They are generous on purpose: images are resized and re-encoded
// (processImage) and videos transcoded to max 1080p / 30 s (videoProcessor) in the browser, so only the much
// smaller result is uploaded and the server's own limits still apply to that.
export const MAX_IMAGE_MB = 100;
export const MAX_VIDEO_MB = 1024;
// Brand catalogue PDFs are uploaded as they are, so this one must match the server (upload.js) and nginx limits.
export const MAX_CATALOGUE_PDF_MB = 100;

/** "100MB" / "1GB" - for the "file size must be less than ..." messages. */
export const formatSizeLimit = (mb) => (mb >= 1024 && mb % 1024 === 0 ? `${mb / 1024}GB` : `${mb}MB`);

/**
 * Validates file size with customizable limit
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - Maximum size in MB (default MAX_IMAGE_MB)
 * @returns {boolean} - True if file size is valid
 */
export const validateFileSize = (file, maxSizeMB = MAX_IMAGE_MB) => {
  const maxSize = maxSizeMB * 1024 * 1024;
  return file.size <= maxSize;
};
